import { Link } from 'react-router-dom'
import { ArrowRight, ShoppingCart, Smartphone, Zap, Shield, Star, Globe, Calendar, Phone } from 'lucide-react'
import FAQAccordion from '../components/FAQAccordion'
import { getImageDimensions } from '../utils/imageDimensions'
import { useMarqueeScroll } from '../hooks/useMarqueeScroll'
import { useLanguage } from '../contexts/LanguageContext'

const Shopify = () => {
  const { t, language } = useLanguage()

  const features = [
    { icon: ShoppingCart, title: 'E-ticaret Altyapısı', description: 'Güçlü ve ölçeklenebilir Shopify altyapısı' },
    { icon: Smartphone, title: 'Mobil Uyumluluk', description: 'Tüm cihazlarda mükemmel alışveriş deneyimi' },
    { icon: Zap, title: 'Hızlı Performans', description: 'Optimize edilmiş sayfa yükleme süreleri' },
    { icon: Shield, title: 'Güvenli Ödeme', description: 'PCI uyumlu güvenli ödeme sistemleri' },
    { icon: Star, title: 'App Store', description: 'Binlerce uygulama ile genişletilebilir' },
    { icon: Globe, title: 'Çoklu Dil & Para', description: 'Uluslararası satış desteği' }
  ]

  const marqueeRef = useMarqueeScroll()

  return (
    <div className="pt-16 bg-[#f9f9f9] relative">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
      
      {/* Hero Section */}
      <section className="py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.05)] p-8 md:p-16">
            <div className="flex flex-col lg:flex-row gap-16 items-center">
              {/* Left Column */}
              <div className="flex-1 space-y-6">
                <span className="inline-block text-[13px] font-medium text-[#666] bg-[#f3f3f3] px-3 py-1 rounded-full">
                  {language === 'tr' ? 'Shopify E-Ticaret' : language === 'en' ? 'Shopify E-Commerce' : 'Shopify E-commerce'}
                </span>
                <h1 className="text-[clamp(32px,5vw,48px)] font-bold text-[#111] leading-[1.1]">
                  {language === 'tr' ? 'Shopify E-Ticaret Sitesi' : language === 'en' ? 'Shopify E-Commerce Site' : 'Сайт интернет-магазина Shopify'}
                </h1>
                <p className="text-lg text-[#555] max-w-[500px] leading-relaxed">
                  {language === 'tr'
                    ? 'Daha fazla satış yapmanıza yardımcı olmak için tasarlanmış tonlarca özellikle birlikte e-ticarette güçlü olun!'
                    : language === 'en'
                    ? 'Be powerful in e-commerce with tons of features designed to help you make more sales!'
                    : 'Мощные возможности для интернет-магазина: больше продаж и рост бизнеса.'}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center bg-black text-white px-7 py-3.5 rounded-full text-[15px] font-medium hover:bg-gray-800 transition-colors duration-200"
                  >
                    {t('servicePages.common.scheduleMeeting')}
                    <Calendar className="w-4 h-4 ml-2" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center bg-transparent text-gray-700 border border-gray-300 px-7 py-3.5 rounded-full text-[15px] font-medium hover:bg-gray-50 transition-colors duration-200"
                  >
                    {t('servicePages.common.callMe')}
                    <Phone className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>

              {/* Right Column */}
              <div className="flex-1 max-w-[500px] w-full">
                <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <img 
                      src="/images/samed-ordueri.jpeg" 
                      alt="Samed Ordueri" 
                      decoding="async"
                      width={getImageDimensions('/images/samed-ordueri.jpeg')?.width}
                      height={getImageDimensions('/images/samed-ordueri.jpeg')?.height}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-[#111] text-base">Samed Ordueri</div>
                      <div className="text-sm text-[#666]">
                        {language === 'tr' ? 'E-Ticaret Uzmanı' : language === 'en' ? 'E-commerce Specialist' : 'Специалист по e-commerce'}
                      </div>
                    </div>
                  </div>
                  <h3 className="font-bold text-[#111] text-lg mb-2">
                    {language === 'tr' ? 'Shopify Mağaza Kurulumu' : language === 'en' ? 'Shopify Store Setup' : 'Настройка магазина Shopify'}
                  </h3>
                  <p className="text-sm text-[#666] mb-6">
                    {language === 'tr'
                      ? 'Online mağazanızı Shopify ile profesyonelce kurun.'
                      : language === 'en'
                      ? 'Launch your online store with Shopify.'
                      : 'Запустите свой интернет-магазин с Shopify.'}
                  </p>
                  <div className="space-y-3 mb-6 text-sm text-[#555]">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path d="M12 6v6l4 2" strokeWidth="2"/></svg>
                      <span>{language === 'tr' ? '30 dk' : language === 'en' ? '30 mins' : '30 мин'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 10l-4 4-4-4" strokeWidth="2" strokeLinecap="round"/></svg>
                      <span>Google Meet</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path d="M12 2a7 7 0 1 0 0 14" strokeWidth="2"/></svg>
                      <span>{language === 'tr' ? 'Avrupa / İstanbul' : language === 'en' ? 'Europe/Istanbul' : 'Европа / Стамбул'}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">15K+</div>
                      <div className="text-xs text-[#999] mt-1">{language === 'tr' ? 'Ürün' : language === 'en' ? 'Products' : 'Товары'}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">24/7</div>
                      <div className="text-xs text-[#999] mt-1">{language === 'tr' ? 'Destek' : language === 'en' ? 'Support' : 'Поддержка'}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">100%</div>
                      <div className="text-xs text-[#999] mt-1">{language === 'tr' ? 'Çalışma süresi' : language === 'en' ? 'Uptime' : 'Аптайм'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* References Section - align with other service pages */}
      <section className="relative py-8 border-t border-gray-200 border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Client Logos Marquee - Single Row (same as other services) */}
          <div ref={marqueeRef} className="w-full overflow-hidden md:overflow-hidden overflow-x-auto md:overflow-x-hidden py-4 relative marquee-container-mobile">
            {/* Fade gradients - hidden on mobile */}
            <div className="hidden md:block absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#f9f9f9] to-transparent z-10 pointer-events-none"></div>
            <div className="hidden md:block absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#f9f9f9] to-transparent z-10 pointer-events-none"></div>
            <div className="flex animate-marquee-right gap-4 md:gap-4">
                {[
                  '/images/brands/ref1.webp',
                  '/images/brands/ref2.webp',
                  '/images/brands/ref3.webp',
                  '/images/brands/ref4.webp',
                  '/images/brands/ref6.webp',
                  '/images/brands/ref7.webp',
                '/images/brands/ref8.webp',
                '/images/brands/veela1.webp',
                '/images/brands/daphnesandals.webp',
                ].map((logo, index) => {
                const d = getImageDimensions(logo)
                return (
                  <div key={`shopify-logo-${index}`} className="marquee-item-mobile flex-shrink-0 bg-white rounded-xl p-3 md:p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                    <img
                      src={logo}
                      alt={`Referans ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      width={d?.width}
                      height={d?.height}
                      className="h-10 md:h-12 max-w-[120px] md:max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                )
              })}
              {/* Duplicate set for seamless loop - hidden on mobile */}
            {[
              '/images/brands/ref1.webp',
              '/images/brands/ref2.webp',
              '/images/brands/ref3.webp',
                '/images/brands/ref4.webp',
                '/images/brands/ref6.webp',
                '/images/brands/ref7.webp',
                '/images/brands/ref8.webp',
              '/images/brands/veela1.webp',
              '/images/brands/daphnesandals.webp',
            ].map((logo, index) => {
                const d = getImageDimensions(logo)
              return (
                  <div key={`shopify-logo-dup-${index}`} className="hidden md:flex flex-shrink-0 bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                <img
                  src={logo}
                      alt={`Referans ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                      width={d?.width}
                      height={d?.height}
                      className="h-12 max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Caption */}
          <div className="mt-4 flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-gray-100 text-xs md:text-sm text-gray-700">
              <span className="text-gray-400 text-[11px] md:text-xs">↗</span>
              <span>{t('servicePages.common.trustedBy')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="bg-[#f9f9f9] py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-[#666] mb-4">Temel Avantajlar</p>
            <h2 className="text-[clamp(32px,5vw,48px)] font-bold text-[#111] leading-[1.1] mb-6">
              Shopify ile e-ticaret işinizi büyütün
            </h2>
            <p className="text-lg text-[#555] max-w-3xl mx-auto mb-8 leading-relaxed">
              Güçlü altyapı, kolay yönetim ve sınırsız özelleştirme imkanı.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-black text-white px-6 md:px-7 py-3 md:py-3.5 rounded-full text-sm md:text-[15px] font-medium hover:bg-gray-800 transition-colors duration-200"
            >
              Başlayın
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
                  </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-10 md:mt-14">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center mb-4">01</div>
              <h3 className="text-xl md:text-2xl font-bold text-[#111] mb-3 leading-tight">Daha Fazla Dönüşüm</h3>
              <p className="text-[#555] leading-relaxed">
                Dönüşüm oranlarını artırmak için optimize edilmiş özellikler ve sınırsız senaryolar.
              </p>
                  </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center mb-4">02</div>
              <h3 className="text-xl md:text-2xl font-bold text-[#111] mb-3 leading-tight">Gelişmiş SEO</h3>
              <p className="text-[#555] leading-relaxed">
                Shopify'in esnekliği ile güçlü SEO altyapısı ve detaylı schema entegrasyonları.
              </p>
                </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center mb-4">03</div>
              <h3 className="text-xl md:text-2xl font-bold text-[#111] mb-3 leading-tight">Sınırsız Otomasyon</h3>
              <p className="text-[#555] leading-relaxed">
                E-posta pazarlama otomasyonları ile satışlarınızı artırın.
              </p>
                </div>
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="bg-white py-24 relative">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-[#666] mb-4">Ek Özellikler</p>
            <h2 className="text-[clamp(32px,5vw,48px)] font-bold text-[#111] leading-[1.1] mb-6">
              Maksimum verimlilik için tasarlandı
            </h2>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-black text-white px-6 md:px-7 py-3 md:py-3.5 rounded-full text-sm md:text-[15px] font-medium hover:bg-gray-800 transition-colors duration-200"
            >
              Başlayın
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-10">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#f3f3f3] flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#111]" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-[#111] mb-2">{feature.title}</h3>
                <p className="text-[#555] leading-relaxed">{feature.description}</p>
                  </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQAccordion
        title="Sıkça Sorulan Sorular"
        subtitle="Shopify e-ticaret hakkında en çok merak edilen sorular"
        faqs={[
          {
            question: "Shopify nedir ve e-ticaret işletmeleri için nasıl bir avantaj sağlar?",
            answer: "Shopify, e-ticaret işletmeleri için kapsamlı bir platform sunar. Kolay kullanım, güvenli ödeme sistemleri ve geniş uygulama mağazası ile işletmenizi hızla büyütmenizi sağlar."
          },
          {
            question: "Shopify ile hangi ödeme yöntemlerini kullanabilirim?",
            answer: "Shopify 100'den fazla ödeme yöntemi destekler. Kredi kartları, PayPal, Apple Pay, Google Pay gibi popüler ödeme seçenekleri mevcuttur."
          },
          {
            question: "Shopify ortalama tema ücretleri nedir?",
            answer: "Shopify tema fiyatları $0-$350 arasında değişir. Ücretsiz temalar mevcut olup, premium temalar genellikle $150-$350 arasındadır."
          },
          {
            question: "Shopify sipariş başı komisyon ücreti var mı?",
            answer: "Shopify Payments kullanıldığında ek işlem ücreti alınmaz. Üçüncü parti ödeme sağlayıcıları için %0.5-2 arası ücret uygulanabilir."
          },
          {
            question: "Shopify yıllık kullanım ücreti nedir?",
            answer: "Shopify Basic planı aylık $29, Shopify planı $79, Advanced planı $299'dur. Yıllık ödemede %10 indirim uygulanır."
          }
        ]}
      />

    </div>
  )
}

export default Shopify














